import { useEffect, useState } from "react";
import "./App.css";
import { PublicClientApplication } from "@azure/msal-browser";
import { Text, PrimaryButton, Stack } from "@fluentui/react";
import Logo from "./img/IOZ.png";
import Connector from "./SignalR/signalR-connection";

import { Participant } from "./Models/Participant";
import { Voting } from "./Models/Voting";

import { finishVoting, updateVote } from "./Services/VoteService";
import { fetchParticipants, createNewParticipant } from "./Services/ParticipantService";

import { Loading } from "./Components/LoadingComponent/Loading";
import { Navbar } from "./Components/NavbarComponent/Navbar";
import { History } from "./Components/HistoyComponent/History";
import { CreateVotingPopup } from "./Components/CreateVotingComponent/CreateVoting";

const msalConfig = {
  auth: {
    clientId: "eac44f26-f2c9-4289-9c0b-262421ae4db3",
    authority: "https://login.microsoftonline.com/cf3767e0-0cba-4bd6-9e43-02cf44292a95",
    redirectUri: process.env.REACT_APP_API_REDIRECTURL,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);
const scopes = ["user.read"];
await msalInstance.initialize();

function App() {
  const { events } = Connector();

  const [msalToken, setMsalToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [history, setHistory] = useState<Voting[]>([]);
  const [votingDetails, setVotingDetails] = useState<undefined | any>(undefined);
  const [isNewVotingDialogVisible, setIsNewVotingDialogVisible] = useState<boolean>(false);
  const [showVoting, setShowVoting] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>();
  const [userEmail, setuserEmail] = useState<string>();
  const [votingId, setVotingId] = useState<string>("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [currentVote, setCurrentVote] = useState<number | null>(null);
  const [currentParticipantId, setcurrentParticipantId] = useState<string | null>("");

  const handleLogin = async () => {
    if (!msalToken) {
      try {
        const tokenResponse = await msalInstance.handleRedirectPromise();
        if (tokenResponse) {
          msalInstance.setActiveAccount(tokenResponse.account);
          window.location.reload();
        } else {
          msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
        }
        const account = msalInstance.getActiveAccount();
        if (account !== null) {
          setUserName(account.name ?? "");
          setuserEmail(account.username ?? "");
        }

        if (account && tokenResponse) {
          console.log("[AuthService] Got valid accountObj and tokenResponse");
          setMsalToken(tokenResponse.accessToken);
        } else if (account) {
          console.log("[AuthService] User has logged in, but no tokens.");
          try {
            const silentTokenResponse = await msalInstance.acquireTokenSilent({
              account,
              scopes,
            });
            setMsalToken(silentTokenResponse.accessToken);
            console.log("[AuthService] Token acquired");
          } catch (error) {
            console.log("[AuthService] Token could not be acquired silently");
            await msalInstance.acquireTokenRedirect({ account, scopes });
          }
        } else {
          console.log(
            "[AuthService] No accountObject or tokenResponse present. User must now login."
          );
          await msalInstance.loginRedirect({ scopes });
        }
      } catch (error) {
        console.error("[AuthService] Failed to handleRedirectPromise()", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  //SignalR
  useEffect(() => {
    const handleFinishReceived = async (result: number, votingId: string) => {
      const Participants: Participant[] = await fetchParticipants(votingId, setVotingDetails);
      setParticipants(Participants);
    };
    const handleJoinGroupReceived = async (votingId: string) => {
      const Participants: Participant[] = await fetchParticipants(votingId, setVotingDetails);
      setParticipants(Participants);
    };
    const handleShowVoteReceived = async (votingId: string) => {
      const Participants: Participant[] = await fetchParticipants(votingId, setVotingDetails);
      setParticipants(Participants);
    };

    events(handleFinishReceived, handleJoinGroupReceived, handleShowVoteReceived);
  }, [events]);

  useEffect(() => {
    handleLogin();
  });

  useEffect(() => {
    async function fetchData() {
      const id = window.location.pathname.substring(1);
      var ParticipantId;
      if (id && userName) {
        const Participants: Participant[] = await fetchParticipants(id, setVotingDetails);
        if (Participants.length > 0) {
          var Participant = Participants.find((p) => p.email === userEmail);
          ParticipantId = Participant?.id;
          setCurrentVote(Participant?.vote ?? -1);
        }

        if (ParticipantId) {
          setcurrentParticipantId(ParticipantId);
        } else {
          createNewParticipant(id, userName ?? "", userEmail ?? "", setcurrentParticipantId);
        }
        setParticipants(Participants);
      }
    }
    fetchData();
  }, [userEmail, userName]);

  useEffect(() => {
    const id = window.location.pathname.substring(1);
    if (id) {
      setVotingId(id);
      setShowVoting(true);
    } else {
      setShowVoting(false);
    }
  }, []);

  useEffect(() => {
    if (votingDetails) {
      document.title = votingDetails.title;
    } else {
      document.title = "Project Estimator";
    }
  }, [votingDetails]);

  const toggleDialog = () => setIsDialogVisible(!isDialogVisible);

  const toggleNewVotingDialog = () => setIsNewVotingDialogVisible(!isNewVotingDialogVisible);

  const handleVote = async (vote: number) => {
    setCurrentVote(vote);
    setParticipants((await updateVote(currentParticipantId, vote, setVotingDetails)) ?? []);
  };

  return isLoading || !msalToken ? (
    <Loading />
  ) : (
    <Stack>
      <Navbar
        toggleDialog={toggleDialog}
        showVoting={showVoting}
        userName={userName ?? ""}
        Logo={Logo}
      />
      {!showVoting ? (
        <Stack horizontalAlign="center" verticalAlign="center" className="OverviewContainer">
          <PrimaryButton
            text="Neue Schätzung"
            onClick={toggleNewVotingDialog}
            className="NewVoting"
          />
        </Stack>
      ) : (
        <Stack>
          <Stack tokens={{ childrenGap: 10 }}>
            <Stack horizontalAlign="center">
              <Text variant="xxLarge" className="votingTitle">
                {votingDetails ? votingDetails.title : "Loading..."}
              </Text>
            </Stack>
            <Stack horizontalAlign="center">
              <PrimaryButton
                text="Abschliessen"
                onClick={() => finishVoting(participants, votingId)}
                className="finishVotingButton"
                disabled={votingDetails !== null && !votingDetails?.isActive}
              />
            </Stack>
            <Stack horizontalAlign="center">
              <Stack className="participantContainer">
                <Stack className="participantGrid" wrap horizontal>
                  {participants.map((participant) => (
                    <Stack horizontalAlign="center" key={participant.id}>
                      <Text className="participantName" variant="xxLarge">
                        {participant.name}
                      </Text>
                      <Stack
                        horizontalAlign="center"
                        verticalAlign="center"
                        className={`participantCard ${
                          votingDetails !== null && votingDetails?.isActive
                            ? participant.vote !== -1 && participant.id !== currentParticipantId
                              ? "participantVoted"
                              : ""
                            : ""
                        } ${participant.id === currentParticipantId ? "participantCurrent" : ""}`}
                      >
                        <Text className="participantVote">
                          {votingDetails !== null && votingDetails?.isActive
                            ? participant.id === currentParticipantId && participant.vote !== -1
                              ? `${participant.vote}`
                              : ""
                            : participant.vote === -1
                            ? "?"
                            : participant.vote}
                        </Text>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          {votingDetails !== null && !votingDetails?.isActive ? (
            <Stack horizontalAlign="center">
              <Stack className="buttonContainer" horizontalAlign="center">
                <Text variant="xxLarge" className="votingInfo">
                  Ergebnisse
                </Text>
                <Stack tokens={{ childrenGap: 10 }}>
                  <Stack horizontal horizontalAlign="center" wrap tokens={{ childrenGap: 20 }}>
                    {Object.values(
                      participants.reduce((acc, participant) => {
                        const voteLabel =
                          participant.vote !== -1 ? participant.vote.toString() : "?";
                        if (!acc[voteLabel]) {
                          acc[voteLabel] = { count: 1, vote: voteLabel };
                        } else {
                          acc[voteLabel].count++;
                        }
                        return acc;
                      }, {} as Record<string, { count: number; vote: string }>)
                    ).map((item, index) => (
                      <Stack horizontalAlign="center">
                        <Stack
                          key={index}
                          className="resultField"
                          horizontalAlign="center"
                          verticalAlign="center"
                        >
                          {item.vote}
                        </Stack>
                        <Text className="resultVoteText">
                          {item.count} {item.count === 1 ? "Vote" : "Votes"}
                        </Text>
                      </Stack>
                    ))}
                    <Stack horizontalAlign="center">
                      <Text className="resultText">Ergebnis</Text>
                      <Text className="resultTextNumber">{votingDetails?.result.toFixed(1)}</Text>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack horizontalAlign="center">
              <Stack className="buttonContainer" horizontalAlign="center">
                <Text variant="xxLarge" className="votingInfo">
                  Wähle eine Karte
                </Text>
                <Stack horizontal wrap tokens={{ childrenGap: 10 }}>
                  {[1, 2, 3, 5, 8, 13, 21, 34, 55].map((vote) => (
                    <PrimaryButton
                      key={vote}
                      text={vote.toString()}
                      onClick={() => handleVote(vote)}
                      style={{
                        backgroundColor: currentVote === vote ? "lightblue" : "",
                      }}
                      className="votingButton"
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
      {isDialogVisible && (
        <History
          toggleDialog={toggleDialog}
          history={history}
          setHistory={setHistory}
          setVotingDetails={setVotingDetails}
          votingDetails={votingDetails}
        />
      )}

      <CreateVotingPopup
        userName={userName}
        isNewVotingDialogVisible={isNewVotingDialogVisible}
        toggleNewVotingDialog={toggleNewVotingDialog}
      />
    </Stack>
  );
}
export default App;
